import { convert24TimeTo12 } from 'infrastructure/functions';

import type { SteppedValues } from './form-sections';

type IRecentWeight = {
  vitalType?: string;
  vitalDate?: string;
  vitalTime?: string;
  vitalValue?: string;
};

const toSentence = <TValue = unknown>(val: TValue) => {
  if (Array.isArray(val)) {
    if (val.every((i) => typeof i === 'string')) return val.join(', ');
    return val.map((i) => ('value' in i ? i.value : i)).join(', ');
  }
  return val;
};

const formatRecentWeights = (arr: IRecentWeight[]) => {
  const output = arr.map((i) => {
    const { vitalType, vitalDate, vitalTime, vitalValue } = i;
    let outputText = '';
    const type = vitalType ? `${vitalType} ` : '';
    const date = vitalDate ? `on ${vitalDate}` : '';
    const time = vitalTime ? ` at ${convert24TimeTo12(`${vitalTime}:00`)}` : '';
    const weight = vitalValue ? ` (${vitalValue} pounds)` : '';
    if (type) outputText += type;
    if (date) outputText += date;
    if (time) outputText += time;
    if (weight) outputText += weight;

    return outputText.trim();
  });
  return output.join(', ');
};

const isStepEmpty = (step: Record<string, unknown>) => {
  return Object.values(step).every((i) => !i);
};

export const generateCkdCcmClinicalNotesTemplate = (values: SteppedValues) => {
  const { STEP1, STEP2, STEP3, STEP4, STEP5 } = values;

  if (
    STEP3?.recentWeights &&
    Array.isArray(STEP3.recentWeights) &&
    !STEP3.recentWeights.every((i) => Object.values(i).length)
  ) {
    STEP3.recentWeights = undefined;
  }

  let text = '';

  // --- STEP 1: CKD History ---
  if (!isStepEmpty(STEP1)) {
    text += 'STEP 1: Initial Overview CKD\n';

    if (STEP1?.howLongTreatedCkd) {
      text += `The patient has been treated for CKD for ${toSentence(
        STEP1.howLongTreatedCkd,
      )}. `;
    }
    if (STEP1?.howLongTreatedCkdNote) {
      text += `Note: ${STEP1.howLongTreatedCkdNote}. `;
      text += '\n';
    } else {
      text += '\n';
    }
    if (STEP1?.effectsOnBody) {
      text += `The patient's understanding of how CKD affects the body is ${toSentence(
        STEP1.effectsOnBody,
      )}. `;
    }
    if (STEP1?.effectsOnBodyNote) {
      text += `Note: ${STEP1.effectsOnBodyNote}. `;
      text += '\n';
    } else {
      text += '\n';
    }
    if (STEP1?.hospitalized) {
      text += `Have you been hospitalized for CKD?: ${toSentence(
        STEP1.hospitalized,
      )}. `;
      text += '\n';
    }
    if (STEP1?.outpatientServices) {
      text += `Have you received outpatient services for CKD?: ${toSentence(
        STEP1.outpatientServices,
      )}. `;
      text += '\n';
    }
    if (STEP1?.familyHistory) {
      text += `Do you have a family history of CKD?: ${toSentence(
        STEP1.familyHistory,
      )}. `;
      text += '\n';
    }
    if (STEP1?.nurseNote) {
      text += `Note: ${STEP1.nurseNote}. `;
    }
  }

  // --- STEP 2: Symptoms & Education ---
  if (!isStepEmpty(STEP2)) {
    text += '\n\n';
    text += 'STEP 2: Signs & Symptoms\n';
    if (STEP2?.recognizesSignsSymptoms) {
      text += `Patient recognizes these Signs and Symptoms of CKD (Limit 4): ${toSentence(
        STEP2.recognizesSignsSymptoms,
      )}.`;
      text += '\n';
    }
    if (STEP2?.snInstructions) {
      text += `SN instructed Patient on these Signs and Symptoms of CKD (Limit 4): ${toSentence(
        STEP2.snInstructions,
      )}. `;
      text += '\n';
    }
    if (STEP2?.notifyProvider) {
      text += `SN instructed Patient to notify Provider if any of these Signs and Symptoms occur: ${toSentence(
        STEP2.notifyProvider,
      )}. `;
      text += '\n';
    }
    if (STEP2?.symptomsTeachBack) {
      text += `SN utilized teach-back method with Patient: ${toSentence(
        STEP2.symptomsTeachBack,
      )}. `;
      text += '\n';
    }
    if (STEP2?.symptomsVerbalizeEducation) {
      text += `Was Patient able to verbalize understanding of the education?: ${toSentence(
        STEP2.symptomsVerbalizeEducation,
      )}. `;
    }
    if (STEP2?.symptomsVerbalizeEducationNote) {
      text += `Note: ${STEP2.symptomsVerbalizeEducationNote}. `;
    }
  }

  // --- STEP 3: Fluid & Weight Management ---
  if (!isStepEmpty(STEP3)) {
    text += '\n\n';
    text += 'STEP 3: Limiting Fluids and Monitors Weight\n';

    if (STEP3?.limitingFluids) {
      text += `SN educated Patient on the importance of limiting fluids (Limit 4): ${toSentence(
        STEP3.limitingFluids,
      )}. `;
      text += '\n';
    }
    if (STEP3?.helpQuenchThirst) {
      text += `To help quench your thirst, you might try to (Limit 4): ${toSentence(
        STEP3.helpQuenchThirst,
      )}. `;
      text += '\n';
    }
    if (STEP3?.fluidsTeachBack) {
      text += `SN utilized teach-back method with Patient: ${toSentence(
        STEP3.fluidsTeachBack,
      )}. `;
      text += '\n';
    }
    if (STEP3?.fluidsVerbalizeEducation) {
      text += `Was Patient able to verbalize understanding of the education?: ${toSentence(
        STEP3.fluidsVerbalizeEducation,
      )}. `;
    }
    if (STEP3?.fluidsVerbalizeEducationNote) {
      text += `Note: ${STEP3.fluidsVerbalizeEducationNote}. `;
      text += '\n';
    }
    if (STEP3?.monitorWeight) {
      text += `Do you monitor weight?: ${toSentence(STEP3.monitorWeight)}. `;
      text += '\n';
    }
    if (STEP3?.monitorWeightImportant) {
      text += `SN instructed Patient on why monitoring weight is important: ${toSentence(
        STEP3.monitorWeightImportant,
      )}. `;
      text += '\n';
    }
    if (STEP3?.haveScale) {
      text += `Do you have a scale?: ${toSentence(STEP3.haveScale)}. `;
      text += '\n';
    }
    if (STEP3?.measureWeight) {
      text += `SN instructed Patient on consistent steps to measure weight: ${toSentence(
        STEP3.measureWeight,
      )}. `;
      text += '\n';
    }
    if (
      STEP3?.recentWeights &&
      Array.isArray(STEP3.recentWeights) &&
      STEP3.recentWeights.every((i) => Object.values(i).length)
    ) {
      text += `What are Patient's recent WEIGHTS?: ${formatRecentWeights(
        STEP3.recentWeights,
      )}. `;
      text += '\n';
    }
    if (STEP3?.dailyWeightMonitoring) {
      text += `Daily Weight Monitoring: ${toSentence(
        STEP3.dailyWeightMonitoring,
      )}. `;
      text += '\n';
    }
    if (STEP3?.weightTeachBack) {
      text += `SN utilized teach-back method with Patient: ${toSentence(
        STEP3.weightTeachBack,
      )}. `;
      text += '\n';
    }
    if (STEP3?.weightVerbalizeEducation) {
      text += `Was Patient able to verbalize understanding of the education?: ${toSentence(
        STEP3.weightVerbalizeEducation,
      )}. `;
      text += '\n';
    }
    if (STEP3?.weightVerbalizeEducationNote) {
      text += `Note: ${STEP3.weightVerbalizeEducationNote}. `;
    }
    if (STEP3?.reinforcementNote) {
      text += `Explain need for reinforcement: ${STEP3.reinforcementNote}. `;
    }
  }

  // --- STEP 4: Nutrition & Diet Counseling ---
  if (!isStepEmpty(STEP4)) {
    text += '\n\n';
    text += 'STEP 4: Nutrition & Diet Counseling\n';

    if (STEP4?.lowSodiumEducation) {
      text += `SN educated Patient on low sodium diet for: ${toSentence(
        STEP4.lowSodiumEducation,
      )}. `;
      text += '\n';
    }
    if (STEP4?.lowSodiumCounselingTips) {
      text += `SN educated Patient on low sodium diet counseling tips: ${toSentence(
        STEP4.lowSodiumCounselingTips,
      )}. `;
      text += '\n';
    }
    if (STEP4?.lowPotassiumEducation) {
      text += `SN educated Patient on low potassium diet: ${toSentence(
        STEP4.lowPotassiumEducation,
      )}. `;
      text += '\n';
    }
    if (STEP4?.lowPotassiumCounselingTips) {
      text += `SN educated Patient on low potassium diet counseling tips: ${toSentence(
        STEP4.lowPotassiumCounselingTips,
      )}. `;
      text += '\n';
    }

    if (STEP4?.limitPhosphorusInDiet) {
      text += `SN educated Patient on tips for limiting phosphorus in the diet for CKD: ${toSentence(
        STEP4.limitPhosphorusInDiet,
      )}. `;
      text += '\n';
    }

    if (STEP4?.lowPhosphorusCounselingTips) {
      text += `SN educated Patient on low phosphorus diet counseling tips for CKD: ${toSentence(
        STEP4.lowPhosphorusCounselingTips,
      )}. `;
      text += '\n';
    }
  }
  // --- STEP 5: Medication Reconciliation ---
  if (!isStepEmpty(STEP5)) {
    text += '\n\n';
    text += 'STEP 5: Medication Reconciliation\n';
    const medicationReconciliationEducation: {
      value: string;
      label: string;
    }[] = [];

    if (STEP5?.medicationReconciliationEducation) {
      medicationReconciliationEducation.push(
        ...STEP5.medicationReconciliationEducation,
      );
    }

    if (STEP5?.discrepancies) {
      medicationReconciliationEducation.push({
        value: `Discrepancies (${STEP5.discrepancies})`,
        label: 'Discrepancies',
      });
    }

    if (medicationReconciliationEducation.length > 0) {
      text += `SN educated Patient on medication reconciliation: ${toSentence(
        medicationReconciliationEducation,
      )}. `;
      text += '\n';
    }

    if (STEP5?.medicationReconciliationCounselingTips) {
      text += `SN educated Patient on medication reconciliation counseling tips: ${toSentence(
        STEP5.medicationReconciliationCounselingTips,
      )}. `;
      text += '\n';
    }
  }
  return text.trim();
};
